import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  MapPin, Users, Clock, Calendar, Star, ChevronRight, ChevronLeft,
  CheckCircle2, Building2, ArrowLeft, DollarSign, Package,
  Sparkles, ExternalLink, Loader2, Tag, X, Info, ShoppingCart, Filter
} from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  'Corporate Event', 'Wedding', 'Birthday Party', 'Holiday Party',
  'Conference', 'Workshop', 'Networking', 'Product Launch',
  'Anniversary', 'Team Building', 'Private Dinner', 'Other'
];

export default function VenueDetailPage() {
  const { slug, venueSlug, state, city, category } = useParams();
  const actualSlug = venueSlug || slug;
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);

  const [bookForm, setBookForm] = useState({
    event_type: 'Corporate Event', event_date: '', start_time: '10:00',
    end_time: '18:00', guest_count: 50, space_id: '', package_id: '',
    addon_selections: [], service_ids: [],
    customer_name: '', customer_email: '', customer_phone: '', notes: '',
  });

  // Modals
  const [detailPkg, setDetailPkg] = useState(null);
  const [detailSvc, setDetailSvc] = useState(null);
  const [svcFilter, setSvcFilter] = useState('');

  // Smart Package Recommendations
  const [recEventType, setRecEventType] = useState('Corporate Event');
  const [recGuests, setRecGuests] = useState(50);
  const [recommendations, setRecommendations] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recSummary, setRecSummary] = useState('');

  // Availability calendar
  const [availMonth, setAvailMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; });
  const [availDays, setAvailDays] = useState([]);
  const [availLoading, setAvailLoading] = useState(false);

  const fetchVenue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/venues/detail/${actualSlug}`);
      setData(res.data);
      if (res.data.spaces?.[0]) {
        setBookForm(p => ({ ...p, space_id: res.data.spaces[0].space_id }));
      }
    } catch (err) {
      console.error('Failed to load venue:', err);
    } finally { setLoading(false); }
  }, [actualSlug]);

  useEffect(() => { fetchVenue(); }, [fetchVenue]);

  const fetchAvailability = useCallback(async () => {
    if (!data?.venue) return;
    setAvailLoading(true);
    try {
      const res = await axios.get(`${API}/venues/${data.venue.venue_id}/monthly-availability?month=${availMonth}`);
      setAvailDays(res.data.days || []);
    } catch (err) { console.error('Availability fetch error:', err); }
    finally { setAvailLoading(false); }
  }, [data?.venue, availMonth]);

  useEffect(() => { if (data?.venue) fetchAvailability(); }, [fetchAvailability]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;
  if (!data?.venue) return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-muted-foreground">Venue not found</p></div>;

  // Defensive destructure — newly imported Places venues can arrive with any of
  // these arrays missing / null before enrichment completes. Coalesce to [] so
  // downstream `.length`, `.map`, `.some`, `.find`, spread etc. never blow up.
  const { venue } = data;
  const spaces = Array.isArray(data.spaces) ? data.spaces : [];
  const addons = Array.isArray(data.addons) ? data.addons : [];
  const packages = Array.isArray(data.packages) ? data.packages : [];
  const preferred_services = Array.isArray(data.preferred_services) ? data.preferred_services : [];
  const photos = venue.photos?.length ? venue.photos : [venue.cover_photo].filter(Boolean);
  const hasCapacity = Number(venue.capacity_min) > 0 || Number(venue.capacity_max) > 0;

  const navigateToBundleCheckout = (opts = {}) => {
    const params = new URLSearchParams({
      venue_id: venue.venue_id, slug,
      space_id: opts.space_id || bookForm.space_id || spaces[0]?.space_id || '',
      guests: String(opts.guest_count || bookForm.guest_count || 50),
      event_type: opts.event_type || bookForm.event_type || 'Corporate Event',
      start: opts.start_time || bookForm.start_time || '10:00',
      end: opts.end_time || bookForm.end_time || '18:00',
    });
    if (opts.package_id) params.set('package_id', opts.package_id);
    if (opts.date) params.set('date', opts.date);
    if (opts.addon_ids) params.set('addon_ids', opts.addon_ids.join(','));
    if (opts.service_ids) params.set('service_ids', opts.service_ids.join(','));
    navigate(`/bundle-checkout?${params.toString()}`);
  };

  const selectedSpace = spaces.find(s => s.space_id === bookForm.space_id);

  const fetchRecommendations = async () => {
    setRecLoading(true);
    try {
      const res = await axios.get(`${API}/venues/${venue.venue_id}/recommend-packages`, {
        params: { event_type: recEventType, guest_count: recGuests }
      });
      setRecommendations(res.data.recommendations || []);
      setRecSummary(res.data.ai_summary || '');
    } catch (err) { console.error('Rec error:', err); }
    finally { setRecLoading(false); }
  };

  // Category filter for services
  const svcCategories = [...new Set(preferred_services?.map(s => s.category).filter(Boolean))];
  const filteredServices = svcFilter
    ? preferred_services?.filter(s => s.category === svcFilter)
    : preferred_services;

  // Schema.org JSON-LD for SEO
  const venueJsonLd = {
    "@context": "https://schema.org",
    "@type": "EventVenue",
    "name": venue.name,
    "description": venue.description,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": venue.address,
      "addressLocality": venue.city,
      "addressRegion": venue.state,
      ...(venue.zip_code && { "postalCode": venue.zip_code }),
      "addressCountry": "US"
    },
    ...(photos.length > 0 && { "image": photos }),
    "maximumAttendeeCapacity": venue.capacity_max,
    ...(venue.amenities?.length > 0 && {
      "amenityFeature": venue.amenities.map(a => ({
        "@type": "LocationFeatureSpecification",
        "name": a,
        "value": true
      }))
    }),
    "url": window.location.href,
    ...(spaces.length > 0 && spaces[0].hourly_rate > 0 && {
      "priceRange": `$${Math.min(...spaces.map(s => s.hourly_rate || 999999))}-$${Math.max(...spaces.map(s => s.full_day_rate || s.hourly_rate || 0))}`
    })
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Venue Rental Partners", "item": `${window.location.origin}/venues` },
      ...(state ? [{ "@type": "ListItem", "position": 2, "name": state.toUpperCase(), "item": `${window.location.origin}/venues/${state}` }] : []),
      ...(city ? [{ "@type": "ListItem", "position": 3, "name": city.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), "item": `${window.location.origin}/venues/${state}/${city}` }] : []),
      ...(category ? [{ "@type": "ListItem", "position": 4, "name": category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), "item": `${window.location.origin}/venues/${state}/${city}/${category}` }] : []),
      { "@type": "ListItem", "position": (state ? 2 : 1) + (city ? 1 : 0) + (category ? 1 : 0) + 1, "name": venue.name }
    ]
  };

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="venue-detail-page">
      <SEO
        title={`${venue.name} — ${venue.city}${venue.state ? ', ' + venue.state : ''} Event Venue`}
        description={(venue.description || `Book ${venue.name} for your next event in ${venue.city}. Capacity up to ${venue.capacity_max || 'N/A'} guests, premium amenities, instant availability via Eventism.`).slice(0, 300)}
        keywords={[
          venue.name,
          `${venue.name} ${venue.city}`,
          `${venue.city} event venue`,
          `${venue.city} wedding venue`,
          `${venue.city} corporate venue`,
          `${venue.city} party venue`,
          `${venue.city} private event space`,
          `event space ${venue.city}`,
          `book venue ${venue.city}`,
          venue.state ? `${venue.state} event venues` : '',
          ...(venue.tags || []),
          'Eventism venues',
        ].filter(Boolean)}
        image={photos[0]}
        type="place"
        structuredData={[venueJsonLd, breadcrumbJsonLd]}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(venueJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      {/* Photo Gallery */}
      <div className="relative">
        {photos.length > 0 ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
            <div className="grid grid-cols-4 gap-2 h-[350px]">
              <div className="col-span-2 row-span-2 relative rounded-l-2xl overflow-hidden">
                <img src={photos[activePhoto] || photos[0]} alt={venue.name} className="w-full h-full object-cover" data-testid="venue-hero-image" />
              </div>
              {photos.slice(1, 5).map((p, i) => (
                <div key={i} className={`relative overflow-hidden cursor-pointer hover:opacity-90 transition-opacity ${i === 1 ? 'rounded-tr-2xl' : i === 3 ? 'rounded-br-2xl' : ''}`} onClick={() => setActivePhoto(i + 1)}>
                  <img src={p} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 pt-4"><div className="h-[250px] bg-card rounded-2xl flex items-center justify-center"><Building2 className="h-16 w-16 text-muted-foreground" /></div></div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/venues')} className="text-xs text-muted-foreground mb-4">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> All Venues
        </Button>

        <div className="lg:flex lg:gap-8">
          {/* Left: Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-indigo-500/20 text-indigo-400 text-[10px] capitalize">{venue.venue_type?.replace('_', ' ')}</Badge>
              {venue.is_featured && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Featured</Badge>}
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground" data-testid="venue-title">{venue.name}</h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[venue.address, venue.city, venue.state].filter(Boolean).join(', ')}</span>
              {hasCapacity && (
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{venue.capacity_min || 0}-{venue.capacity_max || '?'} guests</span>
              )}
            </div>
            <p className="text-base text-muted-foreground mt-4 leading-relaxed">{venue.description}</p>

            {/* Amenities */}
            {venue.amenities?.length > 0 && (
              <div className="mt-6">
                <h2 className="text-sm font-semibold text-foreground mb-2">Amenities</h2>
                <div className="flex flex-wrap gap-1.5" data-testid="venue-amenities">
                  {venue.amenities.map(a => (
                    <span key={a} className="text-[10px] px-2.5 py-1 rounded-full bg-muted text-muted-foreground flex items-center gap-1">
                      <CheckCircle2 className="h-2.5 w-2.5 text-emerald-400" /> {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Spaces */}
            {spaces.length > 0 && (
              <div className="mt-8">
                <h2 className="text-sm font-semibold text-foreground mb-3">Available Spaces</h2>
                <div className="space-y-3" data-testid="venue-spaces">
                {spaces.map(s => (
                  <Card key={s.space_id} className={`bg-muted/60 border-border cursor-pointer transition-all ${bookForm.space_id === s.space_id ? 'border-indigo-500/40 ring-1 ring-indigo-500/20' : 'hover:border-border'}`}
                    onClick={() => setBookForm(p => ({ ...p, space_id: s.space_id }))}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-bold text-foreground">{s.name}</h3>
                            {bookForm.space_id === s.space_id && <Badge className="bg-indigo-500/20 text-indigo-400 text-[8px]">Selected</Badge>}
                          </div>
                          {s.description && <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>}
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                            <span><Users className="h-3 w-3 inline mr-0.5" />{s.capacity_min}-{s.capacity_max}</span>
                            {s.minimum_hours > 0 && <span><Clock className="h-3 w-3 inline mr-0.5" />{s.minimum_hours}hr min</span>}
                          </div>
                          {s.setup_styles?.length > 0 && (
                            <div className="flex gap-1 mt-1.5">{s.setup_styles.map(st => <Badge key={st} className="text-[8px] bg-muted text-muted-foreground">{st}</Badge>)}</div>
                          )}
                        </div>
                        <div className="text-right">
                          {s.hourly_rate > 0 && <p className="text-sm font-bold text-foreground">${s.hourly_rate}<span className="text-[10px] text-muted-foreground">/hr</span></p>}
                          {s.half_day_rate > 0 && <p className="text-[10px] text-muted-foreground">${s.half_day_rate} half-day</p>}
                          {s.full_day_rate > 0 && <p className="text-[10px] text-muted-foreground">${s.full_day_rate} full-day</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                </div>
              </div>
            )}

            {/* ONE-CLICK PACKAGES */}
            {packages.length > 0 && (
              <div className="mt-8">
                <h2 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-amber-400" /> One-Click Packages
                </h2>
                <p className="text-[10px] text-muted-foreground mb-3">Click a package to see details and customize</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="venue-packages">
                  {packages.map(pkg => (
                    <Card key={pkg.package_id} className="bg-muted/60 border-border overflow-hidden group hover:border-indigo-500/30 transition-all cursor-pointer"
                      onClick={() => setDetailPkg(pkg)} data-testid={`package-card-${pkg.package_id}`}>
                      {pkg.cover_photo && <img src={pkg.cover_photo} alt={pkg.name} loading="lazy" className="w-full h-40 object-cover group-hover:scale-[1.02] transition-transform" />}
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          {pkg.is_featured && <Badge className="bg-amber-500/20 text-amber-400 text-[8px]">Popular</Badge>}
                          {pkg.tags?.map(t => <Badge key={t} className="text-[8px] bg-muted text-muted-foreground">{t}</Badge>)}
                        </div>
                        <h3 className="text-base font-bold text-foreground">{pkg.name}</h3>
                        {pkg.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{pkg.description}</p>}
                        <div className="mt-3 space-y-1 text-[10px] text-muted-foreground">
                          <p className="flex items-center gap-1"><Building2 className="h-3 w-3" /> {pkg.space?.name} ({pkg.space?.duration_hours}hrs)</p>
                          {pkg.addons?.length > 0 && <p className="flex items-center gap-1"><Package className="h-3 w-3" /> {pkg.addons.length} add-ons included</p>}
                          {pkg.services?.length > 0 && <p className="flex items-center gap-1 text-indigo-400"><Tag className="h-3 w-3" /> {pkg.services.length} vendor services</p>}
                        </div>
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                          <div>
                            <span className="text-xl font-bold text-emerald-400">${pkg.package_price?.toLocaleString()}</span>
                            {pkg.savings > 0 && <span className="text-[10px] text-lime-400 ml-1.5">Save ${pkg.savings}</span>}
                          </div>
                          <Button size="sm" variant="outline" className="border-border text-xs text-muted-foreground" data-testid={`view-package-${pkg.package_id}`}>
                            <Info className="h-3 w-3 mr-1" /> View Details
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Add-ons */}
            {addons.length > 0 && (
              <div className="mt-8">
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                  <Package className="h-4 w-4 text-emerald-400" /> Available Add-ons
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="venue-addons">
                  {addons.map(a => (
                    <Card key={a.addon_id} className="bg-muted/60 border-border hover:border-emerald-500/30 transition-all cursor-pointer"
                      onClick={() => navigateToBundleCheckout({ addon_ids: [a.addon_id] })} data-testid={`addon-card-${a.addon_id}`}>
                      <CardContent className="p-3">
                        <p className="text-xs font-medium text-foreground">{a.name}</p>
                        {a.description && <p className="text-[9px] text-muted-foreground mt-0.5 line-clamp-1">{a.description}</p>}
                        <p className="text-[10px] text-emerald-400 font-semibold mt-1">${a.price} {a.pricing_type === 'per_unit' ? '/unit' : a.pricing_type === 'per_hour' ? '/hr' : 'flat'}</p>
                        <div className="flex items-center justify-end mt-1.5">
                          <span className="text-[9px] text-indigo-400 flex items-center gap-0.5"><ShoppingCart className="h-2.5 w-2.5" /> Add to Booking</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Preferred Vendor Services with Category Filter */}
            {preferred_services?.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <Tag className="h-4 w-4 text-indigo-400" /> Venue-Approved Services
                  </h2>
                </div>
                <p className="text-[10px] text-muted-foreground mb-3">Click a service for details and customization</p>

                {/* Category Filter */}
                {svcCategories.length > 1 && (
                  <div className="flex flex-wrap gap-1.5 mb-3" data-testid="service-category-filter">
                    <button onClick={() => setSvcFilter('')}
                      className={`px-3 py-1 rounded-full text-[10px] font-medium transition-colors ${!svcFilter ? 'bg-indigo-500 text-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
                      All
                    </button>
                    {svcCategories.map(cat => (
                      <button key={cat} onClick={() => setSvcFilter(cat)}
                        className={`px-3 py-1 rounded-full text-[10px] font-medium capitalize transition-colors ${svcFilter === cat ? 'bg-indigo-500 text-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
                        data-testid={`svc-filter-${cat}`}>
                        {cat}
                      </button>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="preferred-services">
                  {filteredServices?.map(svc => (
                    <Card key={svc.service_id} className="bg-muted/60 border-border hover:border-indigo-500/30 transition-all cursor-pointer"
                      onClick={() => setDetailSvc(svc)} data-testid={`service-card-${svc.service_id}`}>
                      <CardContent className="p-3 flex items-center gap-3">
                        {svc.image_url ? <img src={svc.image_url} alt="" loading="lazy" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" /> :
                          <div className="w-14 h-14 rounded-lg bg-muted flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{svc.name}</p>
                          <p className="text-[10px] text-muted-foreground capitalize">{svc.category}</p>
                          <p className="text-xs text-emerald-400 font-semibold">From ${svc.base_price}</p>
                        </div>
                        <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* House Rules */}
            {venue.house_rules?.length > 0 && (
              <div className="mt-8">
                <h2 className="text-sm font-semibold text-foreground mb-2">House Rules</h2>
                <ul className="space-y-1">
                  {venue.house_rules.map((r, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5"><span className="text-indigo-400 mt-0.5">-</span>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0 mt-6 lg:mt-0 lg:sticky lg:top-20 lg:self-start space-y-4" data-testid="booking-sidebar">
            <Card className="bg-muted/60 border-border">
              <CardContent className="p-4">
                <div className="text-center mb-3">
                  {spaces.some(s => s.hourly_rate > 0) && (
                    <>
                      <p className="text-muted-foreground text-xs">Starting from</p>
                      <p className="text-2xl font-bold text-foreground">
                        ${Math.min(...spaces.filter(s => s.hourly_rate > 0).map(s => s.hourly_rate))}
                        <span className="text-sm text-muted-foreground">/hr</span>
                      </p>
                    </>
                  )}
                </div>
                <Button className="w-full bg-lime-500 hover:bg-lime-600 text-slate-900 font-semibold" onClick={() => navigateToBundleCheckout()} data-testid="book-venue-btn">
                  <Calendar className="h-4 w-4 mr-1.5" /> Request Booking
                </Button>
                <p className="text-[10px] text-muted-foreground text-center mt-2">15% platform fee applies. Free cancellation per venue policy.</p>
              </CardContent>
            </Card>

            {/* Availability Calendar */}
            <Card className="bg-muted/60 border-border" data-testid="availability-calendar">
              <CardContent className="p-4">
                <h3 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-indigo-400" /> Availability</h3>
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => { const [y,m] = availMonth.split('-').map(Number); const d = new Date(y, m-2, 1); setAvailMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); }}
                    className="text-muted-foreground hover:text-foreground p-0.5"><ChevronLeft className="h-3.5 w-3.5" /></button>
                  <span className="text-[10px] font-medium text-muted-foreground">{(() => { const [y,m] = availMonth.split('-').map(Number); return new Date(y,m-1).toLocaleDateString('en-US',{month:'long',year:'numeric'}); })()}</span>
                  <button onClick={() => { const [y,m] = availMonth.split('-').map(Number); const d = new Date(y, m, 1); setAvailMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); }}
                    className="text-muted-foreground hover:text-foreground p-0.5"><ChevronRight className="h-3.5 w-3.5" /></button>
                </div>
                <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                  {['M','T','W','T','F','S','S'].map((d,i) => <div key={i} className="text-center text-[7px] text-muted-foreground font-medium">{d}</div>)}
                </div>
                {availLoading ? (
                  <div className="py-4 text-center"><Loader2 className="h-4 w-4 animate-spin text-indigo-400 mx-auto" /></div>
                ) : (
                  <div className="grid grid-cols-7 gap-0.5" data-testid="avail-grid">
                    {(() => {
                      if (!availDays.length) return null;
                      const dayMap = { monday: 0, tuesday: 1, wednesday: 2, thursday: 3, friday: 4, saturday: 5, sunday: 6 };
                      const offset = dayMap[availDays[0]?.day_of_week] || 0;
                      return Array.from({ length: offset }).map((_, i) => <div key={`e-${i}`} />);
                    })()}
                    {availDays.map(day => {
                      const dayNum = parseInt(day.date.split('-')[2]);
                      const isPast = new Date(day.date) < new Date(new Date().toISOString().split('T')[0]);
                      const colors = { available: 'bg-emerald-500/20 text-emerald-400', partially_booked: 'bg-amber-500/20 text-amber-400', fully_booked: 'bg-red-500/20 text-red-400', blocked: 'bg-muted/40 text-muted-foreground', closed: 'bg-muted/30 text-muted-foreground' };
                      return (
                        <button key={day.date} onClick={() => { if (!isPast && day.status === 'available') navigateToBundleCheckout({ date: day.date }); }}
                          className={`w-full aspect-square rounded text-[9px] font-medium transition-all ${isPast ? 'opacity-30' : day.status === 'available' ? 'cursor-pointer hover:ring-1 hover:ring-emerald-400/50' : ''} ${colors[day.status] || colors.available}`}
                          title={`${day.date}: ${day.status}`} data-testid={`avail-${day.date}`}>
                          {dayNum}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <span className="flex items-center gap-0.5 text-[7px] text-muted-foreground"><span className="w-1.5 h-1.5 rounded bg-emerald-500/40" /> Open</span>
                  <span className="flex items-center gap-0.5 text-[7px] text-muted-foreground"><span className="w-1.5 h-1.5 rounded bg-amber-500/40" /> Partial</span>
                  <span className="flex items-center gap-0.5 text-[7px] text-muted-foreground"><span className="w-1.5 h-1.5 rounded bg-red-500/40" /> Full</span>
                </div>
              </CardContent>
            </Card>

            {/* Smart Recommender */}
            {packages.length > 0 && (
              <Card className="bg-gradient-to-br from-indigo-500/5 to-purple-500/5 border-indigo-500/20" data-testid="smart-recommender">
                <CardContent className="p-4">
                  <h3 className="text-sm font-bold text-foreground flex items-center gap-1.5 mb-3"><Sparkles className="h-4 w-4 text-amber-400" /> Find Your Perfect Package</h3>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Event Type</Label>
                      <Select value={recEventType} onValueChange={setRecEventType}>
                        <SelectTrigger className="bg-muted/80 border-border h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Guest Count</Label>
                      <Input type="number" value={recGuests} onChange={e => setRecGuests(parseInt(e.target.value) || 1)} className="bg-muted/80 border-border h-8 text-xs" data-testid="rec-guest-input" />
                    </div>
                    <Button size="sm" onClick={fetchRecommendations} disabled={recLoading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-xs" data-testid="get-recommendations-btn">
                      {recLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                      Get AI Recommendation
                    </Button>
                  </div>
                  {recSummary && (
                    <div className="mt-3 p-3 rounded-lg bg-muted/60 border border-border" data-testid="ai-recommendation-summary">
                      <p className="text-xs text-muted-foreground leading-relaxed">{recSummary}</p>
                    </div>
                  )}
                  {recommendations?.length > 0 && (
                    <div className="mt-3 space-y-2" data-testid="recommended-packages">
                      {recommendations.map((rec, i) => (
                        <button key={rec.package_id} onClick={() => {
                          const pkg = packages.find(p => p.package_id === rec.package_id);
                          if (pkg) setDetailPkg(pkg);
                        }}
                          className={`w-full text-left p-2.5 rounded-lg border transition-all hover:border-indigo-500/40 ${i === 0 ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-muted/50 border-border'}`} data-testid={`rec-package-${i}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-1.5">
                                {i === 0 && <Badge className="bg-amber-500/20 text-amber-400 text-[7px] px-1">Best Match</Badge>}
                                <p className="text-xs font-semibold text-foreground">{rec.name}</p>
                              </div>
                              <p className="text-[9px] text-muted-foreground mt-0.5">{rec.space_name} | {rec.duration_hours}hrs | {rec.services_count} services</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-bold text-emerald-400">${rec.package_price?.toLocaleString()}</p>
                              {rec.savings > 0 && <p className="text-[8px] text-lime-400">Save ${rec.savings}</p>}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {recommendations?.length === 0 && (
                    <p className="text-[10px] text-muted-foreground mt-3 text-center">No packages match your criteria.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </aside>
        </div>
      </div>

      {/* ===== PACKAGE DETAIL MODAL ===== */}
      <Dialog open={!!detailPkg} onOpenChange={() => setDetailPkg(null)}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="package-detail-modal">
          {detailPkg && (
            <>
              <DialogHeader>
                <DialogTitle className="text-foreground text-lg">{detailPkg.name}</DialogTitle>
              </DialogHeader>
              {detailPkg.cover_photo && <img src={detailPkg.cover_photo} alt="" loading="lazy" className="w-full h-48 object-cover rounded-lg -mt-1" />}
              {detailPkg.description && <p className="text-sm text-muted-foreground mt-2">{detailPkg.description}</p>}

              <div className="space-y-3 mt-3">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-[10px] text-muted-foreground uppercase font-semibold mb-1">Space</p>
                  <p className="text-sm text-foreground">{detailPkg.space?.name}</p>
                  <p className="text-xs text-muted-foreground">{detailPkg.space?.duration_hours} hours | Capacity: {detailPkg.space?.capacity_min}-{detailPkg.space?.capacity_max}</p>
                </div>

                {detailPkg.addons?.length > 0 && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-[10px] text-muted-foreground uppercase font-semibold mb-2">Included Add-ons</p>
                    {detailPkg.addons.map(a => (
                      <div key={a.addon_id} className="flex items-center justify-between text-xs py-1 border-b border-border/50 last:border-0">
                        <span className="text-muted-foreground">{a.name} {a.quantity > 1 ? `x${a.quantity}` : ''}</span>
                        <span className="text-emerald-400">${a.total_price}</span>
                      </div>
                    ))}
                  </div>
                )}

                {detailPkg.services?.length > 0 && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-[10px] text-muted-foreground uppercase font-semibold mb-2">Included Services</p>
                    {detailPkg.services.map(svc => (
                      <div key={svc.service_id} className="flex items-center gap-2 py-1.5 border-b border-border/50 last:border-0">
                        {svc.image_url && <img src={svc.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                        <div className="flex-1">
                          <p className="text-xs text-foreground">{svc.name}</p>
                          <p className="text-[9px] text-muted-foreground capitalize">{svc.category}</p>
                        </div>
                        <p className="text-xs text-emerald-400">${svc.base_price}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div>
                    <p className="text-2xl font-bold text-emerald-400">${detailPkg.package_price?.toLocaleString()}</p>
                    {detailPkg.savings > 0 && <p className="text-[10px] text-lime-400">Save ${detailPkg.savings} vs individual pricing</p>}
                  </div>
                  <Button onClick={() => { setDetailPkg(null); navigateToBundleCheckout({ space_id: detailPkg.space.space_id, package_id: detailPkg.package_id }); }}
                    className="bg-lime-500 hover:bg-lime-600 text-slate-900 font-semibold" data-testid="book-package-from-modal">
                    <ShoppingCart className="h-4 w-4 mr-1.5" /> Book This Package
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== SERVICE DETAIL MODAL ===== */}
      <Dialog open={!!detailSvc} onOpenChange={() => setDetailSvc(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="service-detail-modal">
          {detailSvc && (
            <>
              <DialogHeader>
                <DialogTitle className="text-foreground">{detailSvc.name}</DialogTitle>
              </DialogHeader>
              {detailSvc.image_url && <img src={detailSvc.image_url} alt="" loading="lazy" className="w-full h-40 object-cover rounded-lg" />}
              <div className="space-y-3 mt-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-indigo-500/20 text-indigo-400 text-[10px] capitalize">{detailSvc.category}</Badge>
                  {detailSvc.sub_category && <Badge className="bg-muted text-muted-foreground text-[10px] capitalize">{detailSvc.sub_category}</Badge>}
                </div>
                {detailSvc.description && <p className="text-sm text-muted-foreground">{detailSvc.description}</p>}
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Base Price</span>
                    <span className="font-bold text-emerald-400">${detailSvc.base_price}</span>
                  </div>
                  {detailSvc.pricing_model && (
                    <p className="text-[10px] text-muted-foreground mt-1 capitalize">Pricing: {detailSvc.pricing_model.replace('_', ' ')}</p>
                  )}
                </div>
                <Button className="w-full bg-lime-500 hover:bg-lime-600 text-slate-900 font-semibold"
                  onClick={() => { setDetailSvc(null); navigateToBundleCheckout({ service_ids: [detailSvc.service_id] }); }}
                  data-testid="add-service-to-booking">
                  <ShoppingCart className="h-4 w-4 mr-1.5" /> Add to Booking
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
